import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

interface DisableAccountConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  name: string | undefined;
  isLoading: boolean;
  actionType: "enable" | "disable";
}

export const DisableAccountConfirmation: React.FC<DisableAccountConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  name,
  isLoading,
  actionType,
}) => {
  const isDisable = actionType === "disable";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isDisable ? "bg-red-100" : "bg-green-100"
              }`}
            >
              {isDisable ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              )}
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                {isDisable ? "Disable App Account" : "Enable App Account"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                {isDisable
                  ? "This action will prevent the user from accessing the app."
                  : "This action will grant the user access to the app."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div
            className={`border rounded-lg p-4 ${
              isDisable
                ? "bg-red-50 border-red-200"
                : "bg-green-50 border-green-200"
            }`}
          >
            <p className="text-sm text-gray-900 mb-2">
              Are you sure you want to {isDisable ? "disable" : "enable"} access for:
            </p>
            <div className="bg-white rounded border px-3 py-2">
              <p className="font-medium text-gray-900">
                {name || "Unknown User"}
              </p>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-900">
              This will result in:
            </p>
            <ul className="text-sm text-gray-600 space-y-1 ml-4">
              {isDisable ? (
                <>
                  <li>• Immediate revocation of app login access</li>
                  <li>• User session termination if currently active</li>
                  <li>• Prevention of new data synchronization</li>
                </>
              ) : (
                <>
                  <li>• User can log in to the app immediately</li>
                  <li>• Data synchronization will be enabled</li>
                  <li>• Access to assigned tasks and orders</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant={isDisable ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={isLoading}
            className={`min-w-[120px] ${!isDisable ? "bg-green-600 hover:bg-green-700" : ""}`}
            type="button"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isDisable ? "Disabling..." : "Enabling..."}
              </>
            ) : (
              <>{isDisable ? "Disable Account" : "Enable Account"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
